/**
 * The PIF workflow's fixed identifiers — the one place the Run screen keys the PIF on.
 *
 * These are a contract with the PIF definition that Epic 80432 seeds: they are permanent across
 * versions and must never be renamed (80430 comment 21712790). The confirmed list is section 2 of
 * docs/epics/EPIC-80430-DECISIONS.md; the API mirrors it in PortalAPI.Application/Helpers/PostIfWorkflow.cs,
 * and the two must change together.
 */

/** The PIF definition's Token. The Token — not its stage keys — decides "this is the PIF": a
 *  duplicate copies the stage keys, but never the Token: the API doesn't generate one, so a copy has
 *  whatever Token the duplicate request supplied (the Library sends none) — never a `-copy` suffix. */
export const PIF_DEFINITION_TOKEN = "NewHire-PostIF";

/** The three PIF stages with bespoke runtime screens, by stage key. Once the Token says the
 *  definition is the PIF, these pick the screen. */
export const PIF_STAGE_KEYS = { start: "pif-1", route: "pif-2", details: "pif-3" } as const;

/** The PIF stages the generic renderers handle. Kept apart from PIF_STAGE_KEYS, which means
 *  "has a bespoke screen". A blocked lookup or a rejection ends on `notApproved`; a PIF all three
 *  directors approved whose employee could not be created ends on `employeeNotCreated` instead
 *  (PIF v2, Epic 80430) - both Cancel stages, both closed by the case owner. */
export const PIF_LATER_STAGE_KEYS = {
  deptDirector: "pif-4",
  hrDirector: "pif-5",
  executiveDirector: "pif-6",
  createEmployee: "pif-7",
  /** v2: the Conditional after pif-7 that branches on pif-7.success. Runs by itself. */
  employeeCreated: "pif-employee-created",
  returnPif: "pif-8",
  finalize: "pif-9",
  notApproved: "pif-not-approved",
  /** v2: approved by all three, but pif-7 could not create the employee. */
  employeeNotCreated: "pif-employee-not-created",
} as const;

/** pif-1 field keys. The SSN or Employee ID typed into the lookup is deliberately NOT a field. */
export const PIF_START_FIELDS = {
  /** `PIF_HR_PRE_APPROVAL` values. "Not yet" stops on the Run screen: no case is created. */
  hrPreApproval: "pif.hrPreApproval",
  /** `PIF_LOOKUP_BY` values. */
  lookupBy: "pif.lookupBy",
  /** New hire and rehire only (rehire: carried over, locked). Not stored for an existing employee. */
  dateOfBirth: "pif.dateOfBirth",
} as const;

/** pif-1 variables. Each is backed by a hidden read-only field of the same key, so the Run screen
 *  saves them through save-values like any other answer; they are written by the lookup, never typed. */
export const PIF_VARIABLES = {
  /** Always set, one of `PIF_LOOKUP_FLOWS`. pif-2 routes on it. */
  lookupFlow: "pif.lookupFlow",
  /** Existing-active and rehire; for a new hire, pif-7 captures the created Employee ID into it. */
  employeeId: "pif.employeeId",
  /** Masked last 4 digits, whenever the lookup used an SSN. Never the full SSN. */
  ssnLast4: "pif.ssnLast4",
  /** New hire only: the PostIF applicant record that holds the full SSN (decisions log 4.3). */
  applicantId: "pif.applicantId",
} as const;

/** pif-3 field keys, grouped as in the decisions log. Dropdowns (department, position, reports-to,
 *  rate type, service, client) store the ID, never the label. */
export const PIF_DETAILS_FIELDS = {
  // Identity: new hire types them; rehire carried over and locked; existing employee copied read-only.
  firstName: "pif.firstName",
  lastName: "pif.lastName",
  middleName: "pif.middleName",
  knownName: "pif.knownName",
  suffix: "pif.suffix",

  // Contact: new hire and rehire only, not collected for an existing employee.
  address1: "pif.address1",
  address2: "pif.address2",
  city: "pif.city",
  state: "pif.state",
  zip: "pif.zip",
  homePhone: "pif.homePhone",
  cell: "pif.cell",
  email: "pif.email",

  // Position.
  switchOrAddition: "pif.switchOrAddition",
  department: "pif.department",
  position: "pif.position",
  reportsTo: "pif.reportsTo",
  firstWorkingDay: "pif.firstWorkingDay",
  hoursPerWeek: "pif.hoursPerWeek",
  employmentType: "pif.employmentType",
  service: "pif.service",
  client: "pif.client",

  // Pay. Rate is flag-only against the band, never blocked (decisions log 3.1).
  rateType: "pif.rateType",
  rate: "pif.rate",
  approverNotes: "pif.approverNotes",

  // Driving positions only (decisions log 3.3).
  hasLicense: "pif.hasLicense",
  willOperateVehicle: "pif.willOperateVehicle",
  licenseNumber: "pif.licenseNumber",

  // Additional.
  emergencyContactNumber: "pif.emergencyContactNumber",
  referralSource: "pif.referralSource",
  // Display names saved with the IDs, read-only (Pooja, 2026-10-08): the approval summary reads these,
  // so an approver needs no PostIF access and sees what was requested even after a rename.
  departmentName: "pif.departmentName",
  positionName: "pif.positionName",
  reportsToName: "pif.reportsToName",
  rateTypeName: "pif.rateTypeName",
} as const;

/** `pif.lookupFlow` values, which are also the pif-2 path labels. */
export const PIF_LOOKUP_FLOWS = {
  existingActive: "existing-active",
  rehire: "rehire",
  new: "new",
  blocked: "blocked",
} as const;

export type PifLookupFlow = (typeof PIF_LOOKUP_FLOWS)[keyof typeof PIF_LOOKUP_FLOWS];

/** `pif.hrPreApproval` option values. */
export const PIF_HR_PRE_APPROVAL = { yes: "yes", notYet: "not-yet" } as const;

/** `pif.lookupBy` option values. */
export const PIF_LOOKUP_BY = { ssn: "ssn", employeeId: "employee-id" } as const;

export type PifLookupBy = (typeof PIF_LOOKUP_BY)[keyof typeof PIF_LOOKUP_BY];

/** `pif.switchOrAddition` option values — existing employees only (decisions log 3.2). */
export const PIF_SWITCH_OR_ADDITION = { switch: "switch", addition: "addition" } as const;

/** `pif.employmentType` option values. */
export const PIF_EMPLOYMENT_TYPES = { fullTime: "ft", partTime: "pt" } as const;

/** `pif.referralSource` option values — today's PostIF referral list (P6, Pooja on 80430,
 *  2026-10-08). Mirrors PostIfWorkflow.ReferralSources in the API's PIF definition. */
export const PIF_REFERRAL_SOURCES = {
  employeeReferral: "employee-referral",
  jobBoard: "job-board",
  walkIn: "walk-in",
  other: "other",
} as const;

/** True only for the PIF definition itself, never a copy of it ("never render the PIF specialized
 *  screens for a non-PIF definition"). Case-insensitive, as the database's uniqueness check is. */
export const isPifDefinitionToken = (token: string | null | undefined): boolean =>
  token?.trim().toLowerCase() === PIF_DEFINITION_TOKEN.toLowerCase();
